import IntlMessageFormat, { Formats } from "intl-messageformat";
import {
  MessageFormatElement,
  SelectElement,
  PluralElement,
  isLiteralElement,
  isSelectElement,
  isPluralElement,
  isNumberElement,
  isPoundElement,
  isDateElement,
  isTimeElement,
  TYPE,
  NumberSkeleton,
  NumberElement,
} from "@formatjs/icu-messageformat-parser";
import * as parser from "@formatjs/icu-messageformat-parser";
import memoize from "fast-memoize";
import { parseNumberSkeleton } from "@formatjs/icu-skeleton-parser";

const formatters = {
  getNumberFormat: memoize(
    (locale, opts) => {
      if (locale === "es-ES") {
        opts = opts ?? {}
        if (opts.useGrouping === undefined) {
          opts.useGrouping = true
        }
      }
      return new Intl.NumberFormat(locale, opts)
    }
  ),
  getDateTimeFormat: memoize(
    (locale, opts) => new Intl.DateTimeFormat(locale, opts)
  ),
  getPluralRules: memoize((locale, opts) => new Intl.PluralRules(locale, opts)),
};

interface ICUMessageArgument {
  name: string;
  type: string;
  optional: boolean;
}

enum CustomFormatOptions {
  CompactPrecise = "cs-compact-precise",
  PreciseInteger = "cs-precise-integer",
  MaxFractionDigits = "cs-max-fraction-digits",
  MinFractionDigits = "cs-min-fraction-digits",
  Scale = "cs-scale",
  CurrencyDisplay = "cs-currency-display",
}

function convertNumberSkeletonToNumberFormatOptionsWithCustomOverrides(
  elem: NumberElement,
  args?: number[]
): Partial<Formats> {
  const tokens = (elem.style as NumberSkeleton).tokens;
  // @ts-ignore
  const value = args && args[elem.value];
  let result: any = parseNumberSkeleton(tokens);
  for (const token of tokens) {
    switch (token.stem) {
      case CustomFormatOptions.CompactPrecise:
        if (value) {
          const val = Number(value);
          if (val < 1000 && val > -1000) {
            result.minimumFractionDigits = 2;
            result.maximumFractionDigits = 2;
          } else {
            result.notation = "compact";
            result.compactDisplay = "short";
            result.minimumFractionDigits = 0;
            result.maximumFractionDigits = 1;
          }
        }
        break;
      case CustomFormatOptions.PreciseInteger:
        result.minimumFractionDigits = 0;
        result.maximumFractionDigits = 0;
        break;
      case CustomFormatOptions.MaxFractionDigits:
        result.maximumFractionDigits = token.options[0];
        break;
      case CustomFormatOptions.MinFractionDigits:
        result.minimumFractionDigits = token.options[0];
        break;
      case CustomFormatOptions.Scale:
        if (value) {
          // @ts-ignore
          args[elem.value] = Number(value) * Number(token.options[0]);
        }
        break;
      case CustomFormatOptions.CurrencyDisplay:
        result.currencyDisplay = token.options[0]
        break;
    }
  }
  return result;
}



type ParsedNumberData = {
  numberFormats: Partial<Formats>;
  runtimeParsing: boolean;
};

const runtimeParsingTokens = (t: { stem: string }) =>
  t.stem === CustomFormatOptions.CompactPrecise ||
  t.stem === CustomFormatOptions.Scale;

function getCustomNumberFormats(template: string, args: any, idGenerator: () => number) {
  function traverseArray(
    elems: MessageFormatElement[],
    data: ParsedNumberData
  ) {
    for (const elem of elems) {
      if (
        elem.type === TYPE.number &&
        elem.style &&
        (elem.style as NumberSkeleton).tokens
      ) {
        const result = convertNumberSkeletonToNumberFormatOptionsWithCustomOverrides(
          elem,
          args
        );
        const guidFormat = idGenerator();
        // @ts-ignore
        data.numberFormats[`customFormat${guidFormat}`] = result;
        if (
          !data.runtimeParsing &&
          (elem.style as NumberSkeleton).tokens.filter(runtimeParsingTokens)
            .length
        ) {
          data.runtimeParsing = true;
        }
        elem.style = `customFormat${guidFormat}`;
      }
      if (elem.type === TYPE.select) {
        for (const option of Object.values(elem.options)) {
          traverseArray(option.value, data);
        }
      }
    }
  }
  const parsedElements = parser.parse(template, {
    formatters,
    ignoreTag: true,
    requiresOtherClause: false
  } as any /* !! */);
  const data = { numberFormats: {}, runtimeParsing: false };
  traverseArray(parsedElements, data);
  return { parsedElements, data };
}

// This wraps IntlMessageFormat and augments it with:
//  - ability to obtain the type definition for the expected arguments
//  - allows to ommit arguments that aren't mandatory when executing the format method
// Note: This is where caching should be considered, so as to not constantly recreate the ICU formatter, which is a costly operation
export class ICUMessageFormatter {
  private formatter: IntlMessageFormat;
  private template: string;
  private runtimeNumberParsing = false;
  private currentCustomFormatId = 0;

  private idGenerator = () => {
    return () => this.currentCustomFormatId++;
  };

  constructor(template: string, culture?: string) {
    this.formatter = this.generateFormatterWithCustomFormats(template, culture);
    this.template = template;
  }

  generateFormatterWithCustomFormats(
    template: string,
    culture: string | undefined,
    args?: any
  ): IntlMessageFormat {
    try {
      const {
        data: { numberFormats, runtimeParsing },
        parsedElements,
      } = getCustomNumberFormats(template, args, this.idGenerator());
      this.runtimeNumberParsing = runtimeParsing;
      const customFormats = Object.keys(numberFormats).length
        ? { number: numberFormats }
        : undefined;
      return new IntlMessageFormat(parsedElements, culture, customFormats, {
        formatters,
        ignoreTag: true,
      });
    } catch (e) {
      console.error(
        `Failed to create formatter for "${template}" and culture ${culture}`
      );
      throw e;
    }
  }

  format(args: { [key: string]: string | null } = {}): string {
    let formatter: IntlMessageFormat = this.formatter;
    const { locale: culture } = this.formatter.resolvedOptions();
    if (this.runtimeNumberParsing) {
      formatter = this.generateFormatterWithCustomFormats(
        this.template,
        culture,
        args
      );
    }
    // If an optional argument is not present in args, add it with a null value because IntlMessageFormat requires its presence
    const argNames: string[] = this.getArguments(formatter).map(
      (arg: ICUMessageArgument) => arg.name
    );
    if (new Set(Object.keys(args)) !== new Set(argNames)) {
      args = argNames.reduce<{ [key: string]: string | null }>((accum, k) => {
        accum[k] = args[k] === undefined ? null : args[k];
        return accum;
      }, {});
    }
    return formatter.format(args) as string;
  }

  getArguments(formatter: IntlMessageFormat): ICUMessageArgument[] {
    type ArgSet = { [key: string]: ICUMessageArgument };
    function traverseArray(ast: MessageFormatElement[], args: ArgSet): ArgSet {
      for (const elem of ast) {
        traverseElem(elem, args);
      }
      return args;
    }

    function traverseElem(elem: MessageFormatElement, args: ArgSet): ArgSet {
      if (isLiteralElement(elem) || isPoundElement(elem)) {
        return args;
      }

      let arg: ICUMessageArgument;
      if (elem.value in args) {
        arg = args[elem.value];
      } else {
        let type = "string";
        if (isNumberElement(elem)) {
          type = "number";
        } else if (isDateElement(elem) || isTimeElement(elem)) {
          type = "Date";
        }
        arg = { name: elem.value, type, optional: false };
        args[elem.value] = arg;
      }

      if (isSelectElement(elem) || isPluralElement(elem)) {
        let optionElem: SelectElement | PluralElement;
        optionElem = isSelectElement(elem)
          ? (elem as SelectElement)
          : (elem as PluralElement);
        const keys: string[] = [];
        for (const [key, option] of Object.entries(optionElem.options)) {
          keys.push(key);
          traverseArray(option.value, args);
        }
        if (isPluralElement(elem)) {
          arg.type = "number";
        } else {
          if (
            keys.length === 2 &&
            (keys.includes("false") ||
              keys.includes("true")) /* | also look for ["1, 0"] and [1, 2] */
          ) {
            arg.type = "boolean";
          } else if (arg.name === 'currency') {
            arg.type = "ConsumerCurrency";
          } else {
            arg.type = keys.map((key) => `"${key}"`).join(" | ");
            arg.optional = "other" in optionElem.options;
          }
        }
      }
      return args;
    }
    return Object.values(traverseArray(formatter.getAst(), {} as ArgSet));
  }

  get type(): string {
    const args = this.getArguments(this.formatter);
    const optional = args.reduce((v, arg) => v && arg.optional, true);
    return [
      optional ? "(args?:" : "(args:",
      " {",
      args
        .map((arg) => `${arg.name}${arg.optional ? "?" : ""}: ${arg.type}`)
        .join(", "),
      "}) => string",
    ].join("");
  }

  get typeImpl(): string {
    const args = this.getArguments(this.formatter);
    const optional = args.reduce((v, arg) => v && arg.optional, true);
    let argType = "{ " + args
      .map((arg) => `${arg.name}${arg.optional ? "?" : ""}: ${arg.type}`)
      .join(", ")
      + " }";

    if (optional) argType = argType + " | undefined";
    return `createFormat<${argType}>(${JSON.stringify(this.template)})`;
  }

  get ast(): MessageFormatElement[] {
    return this.formatter.getAst();
  }
  get args() {
    const args = this.getArguments(this.formatter);
    return args;
  }
}
